import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

const PIN_LENGTH = 4;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputRefs = useRef([]);

  const focusIndex = (i) => {
    const el = inputRefs.current[i];
    if (el) {
      el.focus();
      el.select();
    }
  };

  const setAt = (i, v) => {
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    return next;
  };

  const submitPin = async (pin) => {
    setSubmitting(true);
    setError("");
    try {
      const u = await login(pin);
      navigate(u.role === "limited" ? "/my-timecard" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDigits(Array(PIN_LENGTH).fill(""));
      setTimeout(() => focusIndex(0), 0);
    } finally {
      setSubmitting(false);
    }
  };

  const onChange = (e, i) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      setAt(i, "");
      return;
    }
    // Handle paste of full PIN into a single box
    if (raw.length >= PIN_LENGTH && i === 0) {
      const filled = raw.slice(0, PIN_LENGTH).split("");
      const next = [...filled, ...Array(PIN_LENGTH - filled.length).fill("")];
      setDigits(next);
      if (next.every((d) => d)) submitPin(next.join(""));
      return;
    }
    const ch = raw.slice(-1);
    const next = setAt(i, ch);
    if (i < PIN_LENGTH - 1) focusIndex(i + 1);
    if (next.every((d) => d)) submitPin(next.join(""));
  };

  const onKeyDown = (e, i) => {
    if (e.key === "Backspace") {
      if (digits[i]) {
        setAt(i, "");
      } else if (i > 0) {
        focusIndex(i - 1);
        setAt(i - 1, "");
      }
      e.preventDefault();
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) {
      focusIndex(i - 1);
      e.preventDefault();
    }
    if (e.key === "ArrowRight" && i < PIN_LENGTH - 1) {
      focusIndex(i + 1);
      e.preventDefault();
    }
    if (e.key === "Enter") {
      const pin = digits.join("");
      if (pin.length === PIN_LENGTH) submitPin(pin);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const pin = digits.join("");
    if (pin.length === PIN_LENGTH) submitPin(pin);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_fleet-manager-482/artifacts/fm874382_Image.jpeg"
            alt="JDMDRE"
            className="h-12 w-auto object-contain"
            data-testid="login-logo"
          />
        </div>

        <h1 className="font-heading text-4xl tracking-tighter font-black text-zinc-950 text-center">
          Enter PIN
        </h1>
        <p className="mt-2 text-sm text-zinc-600 text-center">
          Type your 4-digit code to sign in.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600 block text-center">
              PIN
            </Label>
            <div
              className="mt-3 flex justify-center gap-3"
              data-testid="login-pin-inputs"
              onPaste={(e) => {
                const text = (e.clipboardData || window.clipboardData).getData("text");
                const raw = (text || "").replace(/\D/g, "").slice(0, PIN_LENGTH);
                if (!raw) return;
                e.preventDefault();
                const arr = raw.split("");
                const next = [...arr, ...Array(PIN_LENGTH - arr.length).fill("")];
                setDigits(next);
                if (next.every((d) => d)) submitPin(next.join(""));
              }}
            >
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  autoFocus={i === 0}
                  maxLength={1}
                  value={d}
                  disabled={submitting}
                  onChange={(e) => onChange(e, i)}
                  onKeyDown={(e) => onKeyDown(e, i)}
                  data-testid={`login-pin-digit-${i}`}
                  className="w-14 h-16 text-center text-3xl font-mono font-black border border-zinc-300 rounded-sm bg-white text-zinc-950 focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7]"
                />
              ))}
            </div>
          </div>
          {error && (
            <div
              className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-sm text-center"
              data-testid="login-error"
            >
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting || digits.join("").length !== PIN_LENGTH}
            data-testid="login-submit-btn"
            className="w-full rounded-sm bg-[#002FA7] hover:bg-[#002075] text-white font-semibold"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <div className="mt-8 pt-6 border-t border-zinc-200 text-xs text-zinc-500 text-center">
          Forgot your PIN? Ask your admin to set a new one for you.
        </div>
      </div>
    </div>
  );
}
